import React, { useState, useMemo, useRef } from 'react';
import { fmtM, fmtDate, todayStr, calcCaja, ventasMes, totalCostosFijos, calcGananciaSemana, SOCIOS, COLORS_SOCIOS } from '../utils';
import { showToast } from '../components/UI';
import { getDb } from '../firebase';
import { ref, set } from 'firebase/database';

// ── HELPERS ──
function lsKey(k) { return 'farm2-' + k; }
function lsClear(k) { try { localStorage.removeItem(lsKey(k)); } catch {} }

const COLLECTIONS = ['movimientos','compras','costosMes','semanas','socios','inversiones','cuentasBancarias'];

// ─────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────
export default function Reportes({ store, setCol }) {
  const [tab, setTab] = useState('reportes'); // 'reportes' | 'migrar' | 'reset'

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, background: 'var(--surf2)', borderRadius: 10, padding: 4, width: 'fit-content' }}>
        {[
          { id: 'reportes', label: '📄 Estados Contables' },
          { id: 'migrar',   label: '📥 Migrar Datos' },
          { id: 'reset',    label: '🗑️ Resetear Base' },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '8px 18px', borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 12.5, fontWeight: 700,
            background: tab === t.id ? 'var(--surf)' : 'transparent',
            color: tab === t.id ? 'var(--tx)' : 'var(--tx3)',
            boxShadow: tab === t.id ? '0 1px 4px rgba(0,0,0,.08)' : 'none',
            transition: 'all .15s'
          }}>{t.label}</button>
        ))}
      </div>

      {tab === 'reportes' && <TabReportes store={store} />}
      {tab === 'migrar'   && <TabMigrar store={store} setCol={setCol} />}
      {tab === 'reset'    && <TabReset store={store} setCol={setCol} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// TAB: ESTADOS CONTABLES
// ─────────────────────────────────────────────────────────────
function TabReportes({ store }) {
  const { movimientos, compras, costosMes, semanas, socios, cuentasBancarias } = store;
  const [periodo, setPeriodo] = useState(todayStr().slice(0, 7));
  const [preview, setPreview] = useState(null); // 'resultado' | 'flujo' | 'posicion'

  // ── Calcular período seleccionado ──
  const stats = useMemo(() => {
    const mesKey = periodo;

    // Ventas del mes
    const ventasMesVal = Object.values(movimientos || {})
      .filter(m => m.tipo === 'venta' && m.fecha?.slice(0, 7) === mesKey)
      .reduce((a, m) => a + (parseFloat(m.monto) || 0), 0);

    // Gastos variables del mes
    const gastosVar = Object.values(movimientos || {})
      .filter(m => m.tipo === 'gasto' && m.fecha?.slice(0, 7) === mesKey)
      .reduce((a, m) => a + (parseFloat(m.monto) || 0), 0);

    // Compras del mes
    const comprasMes = Object.values(compras || {})
      .filter(c => c.fecha?.slice(0, 7) === mesKey)
      .reduce((a, c) => a + (parseFloat(c.monto) || 0), 0);

    // Costos fijos del mes — reconstruidos desde movimientos de pago (pago_proveedor)
    // que corresponden a conceptos de costos fijos. Igual lógica que HistorialCostos.
    // También incluye costosMes si el mes actual coincide (para el mes en curso).
    const pagosMes = Object.values(movimientos || {}).filter(m =>
      m.tipo === 'pago_proveedor' && m.fecha?.slice(0, 7) === mesKey
    );
    const costosFijosDesdeMovs = pagosMes.reduce((a, m) => a + (parseFloat(m.monto) || 0), 0);

    // Si es el mes actual, también sumar costos pagados de costosMes que aún no se registraron como movimiento
    const esMessActual = mesKey === todayStr().slice(0, 7);
    const costosFijosDesdeStore = esMessActual
      ? Object.values(costosMes || {}).filter(c => c.pagado).reduce((a, c) => a + (parseFloat(c.monto) || 0), 0)
      : 0;

    // Usar movimientos si hay datos, sino caer en costosMes (mes actual)
    const costosFijos = costosFijosDesdeMovs > 0 ? costosFijosDesdeMovs : costosFijosDesdeStore;

    // Ganancia bruta (margen 34%)
    const ganBruta = ventasMesVal * 0.34;
    const ganNeta = ganBruta - costosFijos - gastosVar;

    // Caja actual (total acumulado, no solo del mes)
    const caja = calcCaja(movimientos);

    // Flujo del mes: ingresos - egresos (excluir ajustes iniciales)
    const TIPOS_EXCLUIR = ['ajuste_inicial', 'ajuste_stock', 'saldo_inicial'];
    const ingresosMes = Object.values(movimientos || {})
      .filter(m => m.signo === '+' && m.fecha?.slice(0, 7) === mesKey && !TIPOS_EXCLUIR.includes(m.tipo))
      .reduce((a, m) => a + (parseFloat(m.monto) || 0), 0);
    const egresosMes = Object.values(movimientos || {})
      .filter(m => m.signo === '-' && m.fecha?.slice(0, 7) === mesKey && !TIPOS_EXCLUIR.includes(m.tipo))
      .reduce((a, m) => a + (parseFloat(m.monto) || 0), 0);
    const flujoNeto = ingresosMes - egresosMes;

    // Compras a cuenta pendientes de pago
    const cuentasPendientes = Object.values(compras || {})
      .filter(c => !c.pagado)
      .reduce((a, c) => a + (parseFloat(c.monto) || 0), 0);

    // Saldos cuentas bancarias
    const saldoBancario = Object.values(cuentasBancarias || {})
      .reduce((a, c) => a + (parseFloat(c.saldo) || 0), 0);

    // Distribución socios del mes
    const distSocios = {};
    Object.values(semanas || {})
      .filter(s => s.cerrada && s.bounds?.desde?.slice(0, 7) === mesKey)
      .forEach(s => {
        Object.entries(s.dist || {}).forEach(([nom, monto]) => {
          distSocios[nom] = (distSocios[nom] || 0) + monto;
        });
      });

    // Préstamos pendientes
    const prestamosPendientes = Object.values(movimientos || {})
      .filter(m => m.tipo === 'prestamo' && !m.cancelado)
      .reduce((a, m) => a + (parseFloat(m.monto) || 0), 0);

    return {
      ventasMesVal, gastosVar, comprasMes, costosFijos,
      ganBruta, ganNeta,
      caja, ingresosMes, egresosMes, flujoNeto,
      cuentasPendientes, saldoBancario,
      distSocios, prestamosPendientes,
    };
  }, [movimientos, compras, costosMes, semanas, cuentasBancarias, periodo]);

  const mesLabel = () => {
    const [y, m] = periodo.split('-');
    const meses = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
    return `${meses[parseInt(m)-1]} ${y}`;
  };

  function printEstado(tipo) {
    setPreview(tipo);
    setTimeout(() => window.print(), 400);
  }

  const cardStyle = {
    background: 'var(--surf)', border: '1px solid var(--border)',
    borderRadius: 12, padding: 20, marginBottom: 14
  };
  const rowStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid var(--border)' };
  const totalRowStyle = { ...rowStyle, borderBottom: 'none', borderTop: '2px solid var(--border2)', paddingTop: 10, marginTop: 4, fontWeight: 700 };

  return (
    <div>
      {/* Selector de período */}
      <div style={{ ...cardStyle, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', marginBottom: 4 }}>Período</div>
          <input type="month" value={periodo} onChange={e => setPeriodo(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border2)', fontSize: 14, background: 'var(--surf)', color: 'var(--tx)', outline: 'none' }} />
        </div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--tx)' }}>{mesLabel()}</div>
          <div style={{ fontSize: 11, color: 'var(--tx3)' }}>Seleccioná el mes para generar los estados contables</div>
        </div>
      </div>

      {/* 3 botones de reporte */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 24 }}>
        {[
          { id: 'resultado', icon: '📊', title: 'Estado de Resultados', sub: 'Ventas, costos, ganancia neta', color: 'var(--grn)', colorL: 'var(--grn-l)' },
          { id: 'flujo',     icon: '💸', title: 'Flujo de Fondos',      sub: 'Ingresos, egresos, saldo', color: 'var(--blu)', colorL: 'var(--blu-l)' },
          { id: 'posicion',  icon: '🏦', title: 'Posición Financiera',  sub: 'Activos, pasivos, patrimonio', color: 'var(--pur)', colorL: 'var(--pur-l)' },
        ].map(r => (
          <div key={r.id} style={{ background: 'var(--surf)', border: `1px solid var(--border)`, borderRadius: 12, padding: 18, cursor: 'pointer' }}
               onClick={() => setPreview(preview === r.id ? null : r.id)}>
            <div style={{ fontSize: 24, marginBottom: 8 }}>{r.icon}</div>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 3, color: 'var(--tx)' }}>{r.title}</div>
            <div style={{ fontSize: 11, color: 'var(--tx3)', marginBottom: 14 }}>{r.sub}</div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={e => { e.stopPropagation(); setPreview(r.id); setTimeout(() => window.print(), 400); }}
                style={{ flex: 1, padding: '7px', borderRadius: 7, border: 'none', background: r.color, color: '#fff', cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>
                🖨️ Imprimir PDF
              </button>
              <button onClick={e => { e.stopPropagation(); setPreview(preview === r.id ? null : r.id); }}
                style={{ padding: '7px 10px', borderRadius: 7, border: '1px solid var(--border2)', background: 'var(--surf2)', color: 'var(--tx2)', cursor: 'pointer', fontSize: 11 }}>
                {preview === r.id ? 'Ocultar' : 'Ver'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Preview estados */}
      {preview === 'resultado' && (
        <EstadoResultados stats={stats} periodo={mesLabel()} />
      )}
      {preview === 'flujo' && (
        <FlujoDeFondos stats={stats} periodo={mesLabel()} movimientos={movimientos} compras={compras} costosMes={costosMes} />
      )}
      {preview === 'posicion' && (
        <PosicionFinanciera stats={stats} periodo={mesLabel()} compras={compras} movimientos={movimientos} cuentasBancarias={cuentasBancarias} />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// ESTADO DE RESULTADOS
// ─────────────────────────────────────────────────────────────
function EstadoResultados({ stats, periodo }) {
  const { ventasMesVal, ganBruta, costosFijos, gastosVar, ganNeta, distSocios } = stats;
  const CMV = ventasMesVal * 0.66; // costo de mercadería vendida estimado
  const totalDistSocios = Object.values(distSocios).reduce((a, b) => a + b, 0);

  return (
    <div className="print-section" style={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 12, padding: 24, marginBottom: 14 }}>
      <PrintHeader titulo="Estado de Resultados" periodo={periodo} />
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <tbody>
          <TablaSec label="INGRESOS" />
          <TablaRow label="Ventas brutas" value={ventasMesVal} />
          <TablaRow label="(-) Costo de Mercadería Vendida (est. 66%)" value={-CMV} indent />
          <TablaTotal label="GANANCIA BRUTA" value={ganBruta} />

          <TablaSec label="GASTOS OPERATIVOS" />
          <TablaRow label="Costos fijos del período" value={-costosFijos} indent />
          <TablaRow label="Gastos variables" value={-gastosVar} indent />
          <TablaTotal label="GANANCIA OPERATIVA NETA" value={ganNeta} highlight />

          {totalDistSocios > 0 && <>
            <TablaSec label="DISTRIBUCIÓN A SOCIOS" />
            {Object.entries(distSocios).map(([nom, monto]) => (
              <TablaRow key={nom} label={`Dist. ${nom}`} value={-monto} indent />
            ))}
            <TablaTotal label="RESULTADO RETENIDO" value={ganNeta - totalDistSocios} />
          </>}
        </tbody>
      </table>
      <PrintFooter />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// FLUJO DE FONDOS
// ─────────────────────────────────────────────────────────────
function FlujoDeFondos({ stats, periodo, movimientos, compras, costosMes }) {
  const { ingresosMes, egresosMes, flujoNeto, caja } = stats;

  // Agrupar ingresos por tipo
  const ingresosPorTipo = {};
  Object.values(movimientos || {}).filter(m => m.signo === '+').forEach(m => {
    const k = m.tipo || 'otros';
    ingresosPorTipo[k] = (ingresosPorTipo[k] || 0) + (parseFloat(m.monto) || 0);
  });
  // Agrupar egresos por tipo
  const egresosPorTipo = {};
  Object.values(movimientos || {}).filter(m => m.signo === '-').forEach(m => {
    const k = m.tipo || 'otros';
    egresosPorTipo[k] = (egresosPorTipo[k] || 0) + (parseFloat(m.monto) || 0);
  });

  const tipoLabel = { venta: 'Ventas', compra: 'Pago compras', gasto: 'Gastos variables', prestamo: 'Préstamos', devolucion: 'Devoluciones', otros: 'Otros' };

  return (
    <div className="print-section" style={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 12, padding: 24, marginBottom: 14 }}>
      <PrintHeader titulo="Estado de Flujo de Fondos" periodo={periodo} />
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <tbody>
          <TablaSec label="ACTIVIDADES OPERATIVAS — INGRESOS" />
          {Object.entries(ingresosPorTipo).map(([tipo, monto]) => (
            <TablaRow key={tipo} label={tipoLabel[tipo] || tipo} value={monto} indent />
          ))}
          <TablaTotal label="TOTAL INGRESOS" value={ingresosMes} />

          <TablaSec label="ACTIVIDADES OPERATIVAS — EGRESOS" />
          {Object.entries(egresosPorTipo).map(([tipo, monto]) => (
            <TablaRow key={tipo} label={tipoLabel[tipo] || tipo} value={-monto} indent />
          ))}
          <TablaTotal label="TOTAL EGRESOS" value={-egresosMes} />

          <TablaSec label="RESUMEN" />
          <TablaRow label="Flujo neto del período" value={flujoNeto} />
          <TablaRow label="Saldo en caja (acumulado)" value={caja.ef} />
          <TablaRow label="Saldo en cuentas (acumulado)" value={caja.cuentas} />
          <TablaTotal label="POSICIÓN DE LIQUIDEZ TOTAL" value={caja.total} highlight />
        </tbody>
      </table>
      <PrintFooter />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// POSICIÓN FINANCIERA
// ─────────────────────────────────────────────────────────────
function PosicionFinanciera({ stats, periodo, compras, movimientos, cuentasBancarias }) {
  const { caja, cuentasPendientes, saldoBancario, prestamosPendientes } = stats;

  // Activos
  const activoEfectivo = caja.ef;
  const activoCuentas = caja.cuentas;
  const activoBancario = saldoBancario;
  const totalActivos = activoEfectivo + activoCuentas + activoBancario;

  // Pasivos
  const pasivoCompras = cuentasPendientes;
  const pasivoPrestamos = prestamosPendientes;
  const totalPasivos = pasivoCompras + pasivoPrestamos;

  // Patrimonio neto
  const patrimonioNeto = totalActivos - totalPasivos;

  return (
    <div className="print-section" style={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 12, padding: 24, marginBottom: 14 }}>
      <PrintHeader titulo="Estado de Posición Financiera" periodo={periodo} />
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <tbody>
          <TablaSec label="ACTIVOS CORRIENTES" />
          <TablaRow label="Efectivo en caja" value={activoEfectivo} indent />
          <TablaRow label="Saldo en cuentas / QR" value={activoCuentas} indent />
          {activoBancario > 0 && <TablaRow label="Saldo bancario registrado" value={activoBancario} indent />}
          <TablaTotal label="TOTAL ACTIVOS" value={totalActivos} />

          <TablaSec label="PASIVOS CORRIENTES" />
          <TablaRow label="Compras a cuenta pendientes de pago" value={-pasivoCompras} indent />
          {pasivoPrestamos > 0 && <TablaRow label="Préstamos / favores pendientes" value={-pasivoPrestamos} indent />}
          <TablaTotal label="TOTAL PASIVOS" value={-totalPasivos} />

          <TablaSec label="PATRIMONIO NETO" />
          <TablaTotal label="PATRIMONIO NETO (Activos − Pasivos)" value={patrimonioNeto} highlight />
        </tbody>
      </table>
      <PrintFooter />
    </div>
  );
}

// ─── Componentes tabla ───
function TablaSec({ label }) {
  return (
    <tr>
      <td colSpan={2} style={{ padding: '14px 0 4px', fontSize: 10, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.08em', borderBottom: '1.5px solid var(--border2)' }}>{label}</td>
    </tr>
  );
}
function TablaRow({ label, value, indent }) {
  const isNeg = value < 0;
  return (
    <tr>
      <td style={{ padding: '5px 0 5px ' + (indent ? '16px' : '0'), color: 'var(--tx2)', fontSize: 12.5 }}>{label}</td>
      <td style={{ padding: '5px 0', textAlign: 'right', color: isNeg ? 'var(--red)' : 'var(--tx)', fontWeight: 500 }}>{fmtM(Math.abs(value))}{isNeg ? ' (-)' : ''}</td>
    </tr>
  );
}
function TablaTotal({ label, value, highlight }) {
  const isNeg = value < 0;
  return (
    <tr style={{ borderTop: '2px solid var(--border2)' }}>
      <td style={{ padding: '8px 0 10px', fontWeight: 700, fontSize: 13, color: highlight ? (isNeg ? 'var(--red)' : 'var(--grn)') : 'var(--tx)' }}>{label}</td>
      <td style={{ padding: '8px 0 10px', textAlign: 'right', fontWeight: 800, fontSize: 14, color: highlight ? (isNeg ? 'var(--red)' : 'var(--grn)') : 'var(--tx)' }}>{fmtM(value)}</td>
    </tr>
  );
}
function PrintHeader({ titulo, periodo }) {
  return (
    <div style={{ marginBottom: 20, paddingBottom: 16, borderBottom: '2px solid var(--border2)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontSize: 11, color: 'var(--tx3)', marginBottom: 2, textTransform: 'uppercase', letterSpacing: '.08em' }}>Farmacia Yacanto</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--tx)' }}>{titulo}</div>
          <div style={{ fontSize: 12, color: 'var(--tx2)', marginTop: 3 }}>Período: {periodo}</div>
        </div>
        <div style={{ textAlign: 'right', fontSize: 11, color: 'var(--tx3)' }}>
          <div>Fecha de emisión</div>
          <div style={{ fontWeight: 600, color: 'var(--tx2)' }}>{fmtDate(todayStr())}</div>
        </div>
      </div>
    </div>
  );
}
function PrintFooter() {
  return (
    <div style={{ marginTop: 24, paddingTop: 12, borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--tx3)' }}>
      <span>Generado por Sistema Farmacia Yacanto</span>
      <span>Documento interno — no tiene validez fiscal</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// TAB: MIGRAR DATOS (Google Sheets)
// ─────────────────────────────────────────────────────────────
function TabMigrar({ store, setCol }) {
  const [sheetUrl, setSheetUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [step, setStep] = useState('url'); // 'url' | 'preview' | 'done'

  // Extraer el ID del sheet de la URL
  function extractSheetId(url) {
    const match = url.match(/\/d\/([a-zA-Z0-9-_]+)/);
    return match ? match[1] : null;
  }

  // Convertir URL de Google Sheets a CSV export
  function toCsvUrl(sheetId, gid = 0) {
    return `https://docs.google.com/spreadsheets/d/${sheetId}/export?format=csv&gid=${gid}`;
  }

  async function fetchSheet() {
    setError('');
    const id = extractSheetId(sheetUrl);
    if (!id) { setError('URL inválida. Asegurate de pegar la URL completa del Google Sheet.'); return; }
    setLoading(true);
    try {
      // Fetch CSV de la primera hoja
      const url = toCsvUrl(id);
      const resp = await fetch(url);
      if (!resp.ok) throw new Error('No se pudo acceder. Asegurate que el Sheet sea público (Anyone with link can view).');
      const text = await resp.text();
      const rows = parseCSV(text);
      if (rows.length < 2) throw new Error('El sheet parece vacío o no tiene datos.');
      setPreview({ id, rows, rawText: text });
      setStep('preview');
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }

  // Parser CSV simple
  function parseCSV(text) {
    const lines = text.trim().split('\n');
    return lines.map(line => {
      const cols = [];
      let cur = '', inQ = false;
      for (let i = 0; i < line.length; i++) {
        const c = line[i];
        if (c === '"') { inQ = !inQ; }
        else if (c === ',' && !inQ) { cols.push(cur.trim()); cur = ''; }
        else { cur += c; }
      }
      cols.push(cur.trim());
      return cols;
    });
  }

  // Intentar mapear las columnas automáticamente
  function detectColumns(headers) {
    const h = headers.map(h => h.toLowerCase());
    return {
      fecha:     h.findIndex(x => x.includes('fecha') || x.includes('date')),
      tipo:      h.findIndex(x => x.includes('tipo') || x.includes('type')),
      concepto:  h.findIndex(x => x.includes('concepto') || x.includes('descripcion') || x.includes('desc')),
      monto:     h.findIndex(x => x.includes('monto') || x.includes('importe') || x.includes('amount') || x.includes('valor')),
      medio:     h.findIndex(x => x.includes('medio') || x.includes('metodo') || x.includes('forma')),
      signo:     h.findIndex(x => x.includes('signo') || x.includes('tipo_mov')),
      proveedor: h.findIndex(x => x.includes('proveedor') || x.includes('supplier')),
    };
  }

  const [colMap, setColMap] = useState({});
  const [targetCol, setTargetCol] = useState('movimientos');

  function initColMap() {
    if (!preview) return;
    const detected = detectColumns(preview.rows[0]);
    setColMap(detected);
  }
  React.useEffect(() => { if (preview) initColMap(); }, [preview]);

  function importData() {
    if (!preview) return;
    const rows = preview.rows.slice(1); // skip headers
    const newItems = {};
    let count = 0;

    rows.forEach((row, i) => {
      if (!row || row.every(c => !c)) return;
      const get = (idx) => idx >= 0 && idx < row.length ? row[idx] : '';

      const fecha   = get(colMap.fecha);
      const tipo    = get(colMap.tipo) || 'venta';
      const concepto = get(colMap.concepto);
      const montoRaw = get(colMap.monto).replace(/[$.]/g, '').replace(',', '.');
      const monto   = parseFloat(montoRaw) || 0;
      const medio   = get(colMap.medio) || 'efectivo';
      const signo   = get(colMap.signo) || '+';
      const proveedor = get(colMap.proveedor);

      if (!fecha || !monto) return;

      const id = 'imp_' + Date.now() + '_' + i;
      newItems[id] = { id, fecha, tipo, concepto, monto, medio, signo, proveedor, importado: true };
      count++;
    });

    if (count === 0) { showToast('No se encontraron filas válidas'); return; }

    // Merge con existentes
    const existing = store[targetCol] || {};
    setCol(targetCol, { ...existing, ...newItems });
    showToast(`✅ ${count} registros importados a "${targetCol}"`);
    setStep('done');
  }

  const headers = preview?.rows[0] || [];

  return (
    <div>
      <div style={{ background: 'var(--blu-l)', border: '1px solid #a0c4e0', borderRadius: 10, padding: 16, marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--blu)', marginBottom: 6 }}>📋 Cómo migrar desde Google Sheets</div>
        <ol style={{ fontSize: 12, color: 'var(--tx2)', paddingLeft: 18, lineHeight: 1.8 }}>
          <li>Abrí tu Google Sheet con los datos históricos</li>
          <li>Hacé que sea público: <b>Compartir → Cualquier persona con el enlace → Lector</b></li>
          <li>Copiá la URL completa y pegala abajo</li>
          <li>Revisá el mapeo de columnas y elegí la colección destino</li>
          <li>Confirmá la importación</li>
        </ol>
        <div style={{ fontSize: 11, color: 'var(--tx3)', marginTop: 8 }}>
          💡 Columnas esperadas: <b>fecha, tipo, concepto, monto, medio, signo, proveedor</b> (en cualquier orden)
        </div>
      </div>

      {step === 'done' ? (
        <div style={{ background: 'var(--grn-l)', border: '1px solid var(--grn)', borderRadius: 10, padding: 24, textAlign: 'center' }}>
          <div style={{ fontSize: 24, marginBottom: 8 }}>✅</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--grn)', marginBottom: 8 }}>Importación completada</div>
          <div style={{ fontSize: 12, color: 'var(--tx2)', marginBottom: 16 }}>Los datos fueron agregados a la base. Podés ver los registros en las secciones correspondientes.</div>
          <button onClick={() => { setStep('url'); setPreview(null); setSheetUrl(''); }}
            style={{ padding: '9px 20px', borderRadius: 8, border: 'none', background: 'var(--grn)', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
            Importar otro sheet
          </button>
        </div>
      ) : (
        <>
          {/* Input URL */}
          <div style={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 12, padding: 20, marginBottom: 16 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', marginBottom: 8 }}>URL del Google Sheet</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input value={sheetUrl} onChange={e => { setSheetUrl(e.target.value); setError(''); }}
                placeholder="https://docs.google.com/spreadsheets/d/..."
                style={{ flex: 1, padding: '10px 14px', borderRadius: 8, border: `1.5px solid ${error ? 'var(--red)' : 'var(--border2)'}`, fontSize: 13, background: 'var(--surf)', color: 'var(--tx)', outline: 'none' }} />
              <button onClick={fetchSheet} disabled={loading || !sheetUrl}
                style={{ padding: '10px 20px', borderRadius: 8, border: 'none', background: sheetUrl ? 'var(--blu)' : 'var(--surf3)', color: sheetUrl ? '#fff' : 'var(--tx3)', cursor: sheetUrl ? 'pointer' : 'default', fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' }}>
                {loading ? '⏳ Cargando...' : '📥 Cargar'}
              </button>
            </div>
            {error && <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 8 }}>⚠️ {error}</div>}
          </div>

          {/* Preview y mapeo */}
          {preview && step === 'preview' && (
            <div style={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 12, padding: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>Vista previa ({preview.rows.length - 1} filas detectadas)</div>
              <div style={{ fontSize: 11, color: 'var(--tx3)', marginBottom: 16 }}>Verificá que el mapeo de columnas sea correcto</div>

              {/* Tabla preview (max 5 filas) */}
              <div style={{ overflowX: 'auto', marginBottom: 20 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                  <thead>
                    <tr>
                      {headers.map((h, i) => (
                        <th key={i} style={{ padding: '6px 10px', background: 'var(--surf2)', borderBottom: '2px solid var(--border2)', textAlign: 'left', fontWeight: 700, color: 'var(--tx2)', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.slice(1, 6).map((row, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                        {row.map((cell, j) => (
                          <td key={j} style={{ padding: '5px 10px', color: 'var(--tx2)' }}>{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mapeo de columnas */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', marginBottom: 12 }}>Mapeo de columnas</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                  {[
                    ['fecha', 'Fecha'],
                    ['tipo', 'Tipo de movimiento'],
                    ['concepto', 'Concepto / descripción'],
                    ['monto', 'Monto / importe'],
                    ['medio', 'Medio de pago'],
                    ['signo', 'Signo (+/-)'],
                  ].map(([field, label]) => (
                    <div key={field}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--tx3)', marginBottom: 4 }}>{label}</div>
                      <select value={colMap[field] ?? -1} onChange={e => setColMap(m => ({ ...m, [field]: parseInt(e.target.value) }))}
                        style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border2)', fontSize: 12, background: 'var(--surf)', color: 'var(--tx)', outline: 'none' }}>
                        <option value={-1}>— No mapear —</option>
                        {headers.map((h, i) => <option key={i} value={i}>{h}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              </div>

              {/* Destino */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', marginBottom: 8 }}>Colección destino</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {[
                    ['movimientos', 'Movimientos / Ventas'],
                    ['compras', 'Compras a cuenta'],
                    ['costosMes', 'Costos fijos'],
                  ].map(([col, label]) => (
                    <button key={col} onClick={() => setTargetCol(col)}
                      style={{ padding: '8px 16px', borderRadius: 8, border: `2px solid ${targetCol === col ? 'var(--grn)' : 'var(--border2)'}`, background: targetCol === col ? 'var(--grn-l)' : 'var(--surf2)', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: targetCol === col ? 'var(--grn)' : 'var(--tx2)' }}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => { setStep('url'); setPreview(null); }}
                  style={{ padding: '10px 20px', borderRadius: 8, border: '1px solid var(--border2)', background: 'var(--surf2)', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--tx2)' }}>
                  Cancelar
                </button>
                <button onClick={importData}
                  style={{ flex: 1, padding: '10px 20px', borderRadius: 8, border: 'none', background: 'var(--grn)', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
                  ✅ Confirmar importación ({preview.rows.length - 1} registros)
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// TAB: RESETEAR BASE
// ─────────────────────────────────────────────────────────────
function TabReset({ store, setCol }) {
  const [confirm, setConfirm] = useState('');
  const [step, setStep] = useState('warn'); // 'warn' | 'confirm' | 'done'
  const [selected, setSelected] = useState(
    Object.fromEntries(COLLECTIONS.map(c => [c, true]))
  );
  const [keepUsers, setKeepUsers] = useState(true);

  const PALABRA = 'BORRAR TODO';

  function doReset() {
    if (confirm !== PALABRA) return;

    // Limpiar colecciones seleccionadas en localStorage
    COLLECTIONS.forEach(col => {
      if (selected[col]) {
        lsClear(col);
        setCol(col, col === 'users' ? [] : {});
      }
    });

    if (!keepUsers) {
      lsClear('users');
      setCol('users', []);
    }

    // Si hay Firebase, también limpiar allí
    const db = getDb();
    if (db) {
      COLLECTIONS.forEach(col => {
        if (selected[col]) {
          set(ref(db, col), col === 'users' ? [] : {}).catch(e => console.error(e));
        }
      });
    }

    showToast('🗑️ Base de datos restablecida');
    setStep('done');
  }

  // Conteos actuales
  const counts = {
    movimientos: Object.keys(store.movimientos || {}).length,
    compras: Object.keys(store.compras || {}).length,
    costosMes: Object.keys(store.costosMes || {}).length,
    semanas: Object.keys(store.semanas || {}).length,
    socios: Object.keys(store.socios || {}).length,
    inversiones: Object.keys(store.inversiones || {}).length,
    cuentasBancarias: Object.keys(store.cuentasBancarias || {}).length,
  };
  const totalRegistros = Object.values(counts).reduce((a, b) => a + b, 0);

  if (step === 'done') return (
    <div style={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 12, padding: 32, textAlign: 'center' }}>
      <div style={{ fontSize: 32, marginBottom: 12 }}>✅</div>
      <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Base restablecida</div>
      <div style={{ fontSize: 13, color: 'var(--tx2)', marginBottom: 20 }}>Las colecciones seleccionadas fueron vaciadas correctamente.</div>
      <button onClick={() => { setStep('warn'); setConfirm(''); setSelected(Object.fromEntries(COLLECTIONS.map(c => [c, true]))); }}
        style={{ padding: '10px 24px', borderRadius: 8, border: 'none', background: 'var(--grn)', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
        Volver
      </button>
    </div>
  );

  return (
    <div>
      {/* Advertencia */}
      <div style={{ background: '#FAE8E6', border: '1.5px solid var(--red)', borderRadius: 10, padding: 16, marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--red)', marginBottom: 4 }}>⚠️ Esta acción no se puede deshacer</div>
        <div style={{ fontSize: 12, color: 'var(--tx2)' }}>
          Antes de resetear, asegurate de haber <b>impreso o guardado los estados contables</b> del período actual.
          Una vez borrados, los datos no se pueden recuperar.
        </div>
      </div>

      {/* Resumen de datos actuales */}
      <div style={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 12, padding: 20, marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 14 }}>Datos actuales en la base ({totalRegistros} registros totales)</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
          {[
            ['movimientos', 'Movimientos / Ventas'],
            ['compras', 'Compras a cuenta'],
            ['costosMes', 'Costos fijos'],
            ['semanas', 'Semanas / Ganancias'],
            ['socios', 'Cuentas socios'],
            ['inversiones', 'Inversiones'],
            ['cuentasBancarias', 'Cuentas bancarias'],
          ].map(([col, label]) => (
            <label key={col} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, background: selected[col] ? '#FAE8E6' : 'var(--surf2)', border: `1px solid ${selected[col] ? 'var(--red)' : 'var(--border)'}`, cursor: 'pointer', transition: 'all .15s' }}>
              <input type="checkbox" checked={selected[col]} onChange={e => setSelected(s => ({ ...s, [col]: e.target.checked }))}
                style={{ accentColor: 'var(--red)', width: 14, height: 14 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 600 }}>{label}</div>
                <div style={{ fontSize: 11, color: 'var(--tx3)' }}>{counts[col]} registros</div>
              </div>
            </label>
          ))}
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, background: 'var(--surf2)', border: '1px solid var(--border)', cursor: 'pointer', marginTop: 8 }}>
          <input type="checkbox" checked={keepUsers} onChange={e => setKeepUsers(e.target.checked)}
            style={{ accentColor: 'var(--grn)', width: 14, height: 14 }} />
          <div>
            <div style={{ fontSize: 12, fontWeight: 600 }}>Mantener usuarios y contraseñas</div>
            <div style={{ fontSize: 11, color: 'var(--tx3)' }}>Recomendado — los usuarios no serán eliminados</div>
          </div>
        </label>
      </div>

      {/* Confirmación */}
      <div style={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 12, padding: 20 }}>
        <div style={{ fontSize: 12, color: 'var(--tx2)', marginBottom: 10 }}>
          Para confirmar, escribí exactamente: <b style={{ color: 'var(--red)' }}>{PALABRA}</b>
        </div>
        <input value={confirm} onChange={e => setConfirm(e.target.value)}
          placeholder={`Escribí "${PALABRA}" para confirmar...`}
          style={{
            width: '100%', padding: '10px 14px', borderRadius: 8,
            border: `1.5px solid ${confirm === PALABRA ? 'var(--red)' : 'var(--border2)'}`,
            fontSize: 13, background: confirm === PALABRA ? '#FAE8E6' : 'var(--surf)',
            color: 'var(--tx)', outline: 'none', marginBottom: 12, boxSizing: 'border-box'
          }} />
        <button onClick={doReset} disabled={confirm !== PALABRA}
          style={{
            width: '100%', padding: 12, borderRadius: 8, border: 'none',
            background: confirm === PALABRA ? 'var(--red)' : 'var(--surf3)',
            color: confirm === PALABRA ? '#fff' : 'var(--tx3)',
            cursor: confirm === PALABRA ? 'pointer' : 'default',
            fontSize: 14, fontWeight: 800, transition: 'all .15s'
          }}>
          🗑️ Restablecer base de datos a cero
        </button>
      </div>
    </div>
  );
}
